// apolo/src/lib/loop/decideNudge.js — P4-T01 (code-tab-lift).
// @arch:pure-engine
//
// PURE decision module extracted from runLocalAgentLoopInner's tool-less nudge gate
// (Feathers: characterize-then-extract; the agentLoop.nudgeGate + P1 nudge-cap
// property are the characterization). It answers ONE question — "given this tool-less
// turn's state, which nudge (if any) should fire, and what flag does applying it
// mutate?" — WITHOUT side effects. The loop keeps the one-shot flags + the actual
// convo.push/continue/return; only the DECISION moved here (L-1 extract-not-rewrite).
//
// Behavior is byte-identical to the inline gate: the gates are checked in the SAME
// order, with the SAME predicates and thresholds (L-3 Engineering floor — do not
// reorder or change a threshold).
import {
  looksLikeAgentStall,
  looksLikePlanOnly,
  looksLikeFakeCompletionSummary,
  looksLikeHallucinatedToolText,
  looksLikeTaskCompleteSummary,
  FIRST_ACTION_NUDGE,
  TOOL_FAILURE_NUDGE,
  WRITE_FILE_NUDGE,
  COMMIT_NUDGE,
} from '../codeAgentMode.js';

/**
 * @typedef {Object} NudgeState
 * @property {string} content              the model's tool-less text this turn
 * @property {boolean} executedAnyTool
 * @property {boolean} wroteFile
 * @property {boolean} committed
 * @property {number} stallNudged          shared stall/first-action/write counter
 * @property {boolean} toolFailureNudged   one-shot flag (already fired?)
 * @property {boolean} commitNudged        one-shot flag (already fired?)
 * @property {boolean} lastToolFailed      a prior tool result this turn started "Erro"
 * @property {number} iteration            0-based loop index
 * @property {number} maxStallNudges       MAX_STALL_NUDGES
 * @property {boolean} [allowCommitNudge] whether this task includes a Git delivery
 * @property {boolean} [readOnly]          loop read-only (EXPLORE/Ask): NUNCA nudge de write/commit
 */

/**
 * Decide the next nudge action for a tool-less turn, or null (fall through to the
 * deliverable/oracle/done path). The descriptor tells the loop what flag to bump and
 * which message to push; a `stop` descriptor means give up with that stopReason.
 *
 * @param {NudgeState} state
 * @returns {null
 *   | { message: string, bumpStall?: true, setToolFailureNudged?: true, setCommitNudged?: true }
 *   | { stop: 'stall' | 'stall_gave_up' }}
 */
export function decideNudge(state) {
  const {
    content, executedAnyTool, wroteFile, committed,
    stallNudged, toolFailureNudged, commitNudged,
    lastToolFailed, iteration, maxStallNudges,
    readOnly = false,
    allowCommitNudge = true,
  } = state;

  const planOrStall = looksLikeAgentStall(content) || looksLikePlanOnly(content)
    || looksLikeFakeCompletionSummary(content)
    || looksLikeHallucinatedToolText(content);
  const needsFirstTool = !executedAnyTool
    && String(content ?? '').trim().length > 48
    && !looksLikeTaskCompleteSummary(content, { executedAnyTool });
  // Dogfood 2026-07-10 — loop READ-ONLY (subagente EXPLORE / Ask) JAMAIS recebe
  // nudge de write/commit: o mis-fire mandava "use apply_edit AGORA" pro EXPLORE,
  // que respondia "sou read-only" — ~20s de round-trip queimado POR nudge. Num
  // loop read-only o TEXTO é o entregável; write/commit não existem.
  const needsWrite = !readOnly && executedAnyTool && !wroteFile && planOrStall;

  // 1) first action (shared counter). With MAX_STALL_NUDGES=1 this fires at most once.
  if (needsFirstTool && stallNudged < maxStallNudges) {
    return { message: FIRST_ACTION_NUDGE, bumpStall: true };
  }
  // 2) tool-failure recovery (one-shot, early iterations only).
  if (!toolFailureNudged && lastToolFailed && iteration < 10 && (planOrStall || !executedAnyTool)) {
    return { message: TOOL_FAILURE_NUDGE, setToolFailureNudged: true };
  }
  // 3) wrote nothing but stalled after acting → push to write.
  if (needsWrite && stallNudged < maxStallNudges) {
    return { message: WRITE_FILE_NUDGE, bumpStall: true };
  }
  // 4) stall before the model has acted at all → one first-action nudge.
  if (!executedAnyTool && planOrStall && stallNudged < maxStallNudges && iteration < 25) {
    return { message: FIRST_ACTION_NUDGE, bumpStall: true };
  }

  // 5) stall AFTER the model has already acted (wrote/read/searched) but stopped
  // again. FASE-12 E3-T01 — antes: stop:'stall' MUDO mesmo com nudge sobrando no
  // orçamento (o run morria sem gastar a correção disponível). Agora: se escreveu
  // e não fechou o ciclo, gasta o COMMIT_NUDGE aqui (o gate 7 nunca era alcançado
  // porque este stop matava o run antes); senão deixa cair pro done/oracle path
  // (que classifica honestamente), em vez de uma morte muda.
  if (executedAnyTool && planOrStall && stallNudged < maxStallNudges && iteration < 25) {
    if (allowCommitNudge && !readOnly && wroteFile && !committed && !commitNudged) {
      return { message: COMMIT_NUDGE, setCommitNudged: true };
    }
    return null;
  }

  // 6) gave up: planned/stalled forever without ever acting.
  if (planOrStall && !executedAnyTool) {
    return { stop: 'stall_gave_up' };
  }
  // 7) close-the-loop: wrote a file, never committed (one-shot). Read-only nunca.
  if (allowCommitNudge && !readOnly && wroteFile && !committed && !commitNudged) {
    return { message: COMMIT_NUDGE, setCommitNudged: true };
  }
  return null;
}

export default decideNudge;
